import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { TEMPLATES_PATHS } from '@common/network/ApiPaths';
import {
  PAGE_ROUTES,
  TEMPLATE_PAGINATION_MODEL,
  VIEW_MODE_OPTIONS,
  TEMPLATES_FILTER_MAPPING,
  EXPORT_CONFIG,
  TEMPLATE_ACTIVE_STATUS,
} from '@common/constants';
import Button from '@common/components/Button/MyButton';
import TedTheme from '@common/providers/ThemeProvider/TedTheme';
import Layout from '@common/components/Layout/Layout';
import Notification, {
  SEVERITY_OPTIONS,
  useNotification,
} from '@common/components/Notification/Notification';

import TemplateList from '@pages/Templates/components/TemplateList';
import TemplateDetails from '@pages/Templates/components/TemplateDetails';
import PreviewDialog from '@pages/TemplateEditor/components/PreviewDialog/PreviewDialog';
import UploadDialog from '@common/components/UploadDialog/UploadDialog';

import { useAuthStore } from '@store/authStore';

import {
  getByPathAndParams,
  postByPathAndData,
  patchByPathAndData,
  deleteByPath,
} from '@services/newServices';

import {
  isDetails,
  isTemplateDeletionAllowed,
  mapDeleteResponse,
  removeHandlebarsEach,
} from '@common/helpers/helpers';
import {
  onTabsTableFilterChange,
  isAdvancedFilterChanged,
  getIsArchivedFilterActive,
} from '@common/helpers/getAdvancedFiltersForMUITable';

import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import merge from 'lodash/merge';
import MyDialog from '@common/components/MyDialog/MyDialog';
import StatusListTable from '@common/components/StatusListTable/StatusListTable';
import PreviewDialogTed from '@pages/TemplateEditor/components/PreviewDialog/PreviewDialogTed';

const INIT_FILTERS = {
  advancedFilters: [],
};

export const getArchiveValue = ({ checkboxSelection, templates }) => {
  const selectedTemplates = templates.filter(({ id }) =>
    checkboxSelection?.some((templateId) => templateId === id),
  );
  if (!selectedTemplates || selectedTemplates.length === 0) return null;

  let hasActive = false;
  let hasInactive = false;

  for (const item of selectedTemplates) {
    if (item.isActive) {
      hasActive = true;
    } else {
      hasInactive = true;
    }

    if (hasActive && hasInactive) {
      return null;
    }
  }

  if (hasActive) return TEMPLATE_ACTIVE_STATUS.ACTIVE;
  return TEMPLATE_ACTIVE_STATUS.ARCHIVED;
};

const removeHandlebarsForEditors = (editor = []) => {
  return editor?.map(({ htmlData, ...rest }) => {
    return { ...rest, html: removeHandlebarsEach(htmlData) };
  });
};

const Templates = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  const [templates, setTemplates] = useState([]);
  const [templatesTotalCount, seTemplatesTotalCount] = useState(0);
  const [templatePreview, setTemplatePreview] = useState({
    isOpen: false,
    data: null,
  });

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState(VIEW_MODE_OPTIONS.DETAILS);
  const [paginationModel, setPaginationModel] = useState(
    TEMPLATE_PAGINATION_MODEL,
  );
  const [isRightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState();
  const [isCopyFinished, setIsCopyFinished] = useState(false);
  const [filterAndSortParams, setFilterAndSortParams] = useState({
    sortBy: null,
    sortDirection: null,
    filters: INIT_FILTERS,
  });
  const [checkboxSelection, setCheckboxSelection] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [showStatuses, showNotifications, handleClose] = useNotification();

  const archiveFilter = getIsArchivedFilterActive(filterAndSortParams);
  const selectionArchiveValue = getArchiveValue({
    checkboxSelection,
    templates,
  });

  const onRightDrawerClose = () => {
    setRightDrawerOpen(false);
    setViewMode(VIEW_MODE_OPTIONS.DETAILS);
  };

  const onRightDrawerOpen = () => {
    setRightDrawerOpen(true);
  };

  const { t } = useTranslation();

  const fetchTemplateDetails = async () => {
    if (selectedTemplate) {
      try {
        let response = await getByPathAndParams({
          path: TEMPLATES_PATHS.GET_BY_ID,
          pathVariables: { id: selectedTemplate },
        });

        setTemplateDetails(response?.data);
      } catch (err) {
        checkErrorStatus(err);
      } finally {
      }
    }
  };

  const checkErrorStatus = (err) => {
    if (err?.status === 403) {
      logout();
    }
  };

  const handleRowClick = (params) => {
    const clickedId = params?.id ?? params?.row?.id;
    if (!clickedId) return;

    setSelectedTemplate(selectedTemplate !== clickedId ? clickedId : null);
  };

  const setEditMode = (status) => {
    setViewMode(status);
  };

  const onNewDocumentClick = () => {
    navigate(`${PAGE_ROUTES.NEW_TEMPLATE}`);
  };

  const onEditTemplate = async (inputData) => {
    try {
      setIsLoading(true);
      const data = {
        name: inputData.name,
        description: inputData.description,
      };

      let res = await patchByPathAndData({
        path: TEMPLATES_PATHS.PATCH_BY_ID,
        pathVariables: { id: templateDetails.id },
        data,
      });

      if (res?.status === 200) {
        setViewMode(VIEW_MODE_OPTIONS.DETAILS);
        showNotifications([
          {
            severity: SEVERITY_OPTIONS.SUCCESS,
            message: t('save_success'),
            show: true,
          },
        ]);
        fetchTemplateDetails();
      }
    } catch (err) {
      checkErrorStatus(err);
      showNotifications([
        {
          severity: SEVERITY_OPTIONS.ERROR,
          message: t('save_error'),
          show: true,
        },
      ]);
    } finally {
      setIsLoading(false);
      setIsCopyFinished(true);
    }
  };

  const goToTemplate = (id) => {
    navigate(`${PAGE_ROUTES.TEMPLATE_EDITOR}?id=${id}`);
  };

  const formatDataForCopying = (templateData) => {
    return {
      name: templateData?.name,
      description: templateData?.description,
      source: templateData?.templateSource,
      dataType: templateData?.dataType,
      type: templateData?.type,
      editor: templateData?.editor || [],
      pdfAdditionalFields: {
        format: templateData?.format,
        orientation: templateData?.orientation,
      },
    };
  };

  const createTemplateCopy = async (restInputData) => {
    try {
      setIsLoading(true);

      const response = await postByPathAndData({
        path: TEMPLATES_PATHS.POST,
        data: formatDataForCopying(restInputData),
      });
      if (response?.data?.id) {
        setViewMode(VIEW_MODE_OPTIONS.DETAILS);
        setTemplateDetails(response?.data);
      }
    } catch (err) {
      checkErrorStatus(err);
      showNotifications([
        {
          severity: SEVERITY_OPTIONS.ERROR,
          message: t('save_error'),
          show: true,
        },
      ]);
    } finally {
      setIsLoading(false);
      setIsCopyFinished(true);
      setDialogConfig();
    }
  };

  const copyHandler = async () => {
    if (templateDetails?.id) {
      createTemplateCopy({
        ...templateDetails,
        name: `${templateDetails.name} copy`,
      });
    }
  };

  const cancelCopyDialog = () => {
    setDialogConfig();
  };

  const openCopyDialog = () => {
    setDialogConfig({
      open: true,
      dialogTitle: t('confirmation_dialog'),
      dialogContent: t('copy_confirmation_dialog'),
      dialogActionsConfig: [
        {
          id: 1,
          startIcon: <HighlightOffIcon />,
          onClick: cancelCopyDialog,
          color: 'error',
          label: t('no_button'),
        },
        {
          id: 2,
          startIcon: <DoneIcon />,
          onClick: copyHandler,
          color: 'primary',
          label: t('yes_button'),
        },
      ],
    });
  };

  const onPreviewCancel = () => {
    setTemplatePreview({ isOpen: false, data: null });
  };

  const onFilterChange = (values) => {
    const advancedFilters = onTabsTableFilterChange(values);

    if (isAdvancedFilterChanged({ advancedFilters, filterAndSortParams })) {
      setFilterAndSortParams({
        ...filterAndSortParams,
        filters: { advancedFilters },
      });
    }
  };

  const onSortChange = (sortModel) =>
    setFilterAndSortParams({
      ...filterAndSortParams,
      sortBy: sortModel[0]?.field || null,
      sortDirection: sortModel[0]?.sort || null,
    });

  const fetchTemplates = async () => {
    merge(filterAndSortParams, {
      page: paginationModel.page,
      size: paginationModel.pageSize,
    });

    try {
      setIsLoading(true);

      const { data } = await getByPathAndParams({
        path: TEMPLATES_PATHS.GET_ALL,
        params: filterAndSortParams,
      });

      const { content = [], totalElements = 0 } = data ?? {};

      setTemplates(content);
      seTemplatesTotalCount(totalElements);
    } catch (err) {
      checkErrorStatus(err);
      setTemplates([]);
      seTemplatesTotalCount(0);
    } finally {
      setIsCopyFinished(false);
      setIsLoading(false);
    }
  };

  const fetchEmailTemplatesPreview = async ({ templateId }) => {
    try {
      setIsLoading(true);
      const { data } = await getByPathAndParams({
        path: TEMPLATES_PATHS.GET_BY_ID,
        pathVariables: { id: templateId },
      });

      setTemplatePreview({
        isOpen: true,
        editor: removeHandlebarsForEditors(data?.editor),
      });
    } catch (err) {
      checkErrorStatus(err);
    }
    setIsLoading(false);
  };

  const fetchExportBlob = (templateIds) => {
    return postByPathAndData({
      path: TEMPLATES_PATHS.EXPORT,
      data: { ids: templateIds },
      responseType: 'blob',
    });
  };

  const downloadBlobFile = ({
    blob,
    fallbackFilename,
    contentDispositionHeader,
  }) => {
    let filename = fallbackFilename;

    if (contentDispositionHeader) {
      const match = contentDispositionHeader.match(/filename="(.+?)"/);
      if (match && match[1]) {
        filename = match[1];
      }
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportTemplates = async () => {
    try {
      setIsLoading(true);

      const response = await fetchExportBlob(checkboxSelection);

      if (response?.status === 200) {
        downloadBlobFile({
          blob: response.data,
          fallbackFilename: 'templates_export.json',
          contentDispositionHeader: response.headers['content-disposition'],
        });

        showNotifications([
          {
            severity: SEVERITY_OPTIONS.SUCCESS,
            message: `${t('export_success')} ${t('export_success_number', {
              count: checkboxSelection?.length,
            })}`,
            show: true,
          },
        ]);
        setCheckboxSelection([]);
      } else {
        showNotifications([
          {
            severity: SEVERITY_OPTIONS.ERROR,
            message: t('export_error'),
            show: true,
          },
        ]);
      }
    } catch (err) {
      console.log('err: ', err);
      checkErrorStatus(err);
      showNotifications([
        {
          severity: SEVERITY_OPTIONS.ERROR,
          message: t('export_error'),
          show: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCheckboxChange = (templateId, checked) => {
    const alreadyIncluded = checkboxSelection.includes(templateId);

    if (
      checked &&
      !alreadyIncluded &&
      checkboxSelection?.length >= EXPORT_CONFIG.MAX_TEMPLATES_TO_EXPORT
    ) {
      showNotifications([
        {
          severity: SEVERITY_OPTIONS.INFO,
          message: t('export_max_info', {
            count: EXPORT_CONFIG.MAX_TEMPLATES_TO_EXPORT,
          }),
          show: true,
        },
      ]);
      return;
    }

    setCheckboxSelection((prevState) => {
      if (checked && !alreadyIncluded) {
        return [...prevState, templateId];
      } else if (!checked && alreadyIncluded) {
        return prevState.filter((id) => id !== templateId);
      }
      return prevState;
    });
  };

  const importTemplatesApi = () => {
    if (!importFile) {
      return null;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    return postByPathAndData({
      path: TEMPLATES_PATHS.IMPORT,
      data: formData,
    });
  };

  const importTemplates = async () => {
    try {
      setIsLoading(true);

      const response = await importTemplatesApi();

      if (response?.status === 201) {
        showNotifications([
          {
            severity: SEVERITY_OPTIONS.SUCCESS,
            message: `${t('import_success')} ${t('import_success_number', {
              count: response?.data?.importedCount || 0,
            })}`,
            show: true,
          },
        ]);

        setImportFile(null);
        setImportDialogOpen(false);
        fetchTemplates();
      } else {
        showNotifications([
          {
            severity: SEVERITY_OPTIONS.ERROR,
            message: t('import_error'),
            show: true,
          },
        ]);
      }
    } catch (err) {
      checkErrorStatus(err);
      showNotifications([
        {
          severity: SEVERITY_OPTIONS.ERROR,
          message: `${t('import_error')} ${err?.response?.data?.error || ''}`,
          show: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUploadChange = (file) => {
    setImportFile(file);
  };

  const openUploadDialog = () => {
    setImportDialogOpen(true);
  };

  const cancelUploadDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
  };

  const onDeleteTemplatesClickConfirm = async () => {
    try {
      setDialogConfig();
      const { data } = await deleteByPath({
        path: TEMPLATES_PATHS.DELETE_BY_IDS,
        pathVariables: {
          ids: checkboxSelection.join(','),
        },
      });

      const deleteTemplatesStatuses = mapDeleteResponse({ data, templates });

      setDialogConfig({
        open: true,
        dialogTitle: t('delete_dialog'),
        dialogContent: <StatusListTable rows={deleteTemplatesStatuses} />,
        maxWidth: 'md',
        dialogActionsConfig: [
          {
            id: 1,
            startIcon: <HighlightOffIcon />,
            onClick: cancelCopyDialog,
            color: 'error',
            label: t('close_button'),
          },
        ],
      });

      setCheckboxSelection([]);

      setTemplates((prevState) =>
        prevState?.reduce((aggArr, currentTemplate) => {
          if (
            !data?.some(
              ({ templateId, allowedDeleteType }) =>
                templateId === currentTemplate?.id &&
                isTemplateDeletionAllowed(allowedDeleteType),
            )
          ) {
            aggArr.push(currentTemplate);
          }

          return aggArr;
        }, []),
      );
    } catch (err) {
      console.log('err: ', err);
      showNotifications([
        {
          severity: SEVERITY_OPTIONS.ERROR,
          message: t('delete_error'),
          show: true,
        },
      ]);
    }
  };

  const onArchiveTemplatesClickConfirm = async (activate) => {
    try {
      setDialogConfig();

      const { data } = await postByPathAndData({
        path: TEMPLATES_PATHS.ARCHIVE_BY_IDS,
        data: {
          ids: checkboxSelection,
          activate,
        },
      });

      if (archiveFilter) {
        setTemplates((prevState) =>
          prevState?.reduce((aggArr, currentTemplate) => {
            if (
              !data?.some((templateId) => templateId === currentTemplate?.id)
            ) {
              aggArr.push(currentTemplate);
            }

            return aggArr;
          }, []),
        );
      } else {
        setTemplates((prevState) =>
          prevState.map((oldTemplate) => {
            if (
              checkboxSelection?.some(
                (templateId) => templateId === oldTemplate?.id,
              )
            ) {
              oldTemplate.isActive = !oldTemplate.isActive;
            }
            return oldTemplate;
          }),
        );
      }
      setCheckboxSelection([]);
    } catch (err) {
      console.log('err: ', err);
      showNotifications([
        {
          severity: SEVERITY_OPTIONS.ERROR,
          message: t('delete_error'),
          show: true,
        },
      ]);
    }
  };

  const onArchive = (activate) => {
    setDialogConfig({
      open: true,
      dialogTitle: activate ? t('active_dialog') : t('archive_dialog'),
      dialogContent: activate
        ? t('active_dialog_desc')
        : t('archive_dialog_desc'),
      dialogActionsConfig: [
        {
          id: 1,
          startIcon: <HighlightOffIcon />,
          onClick: cancelCopyDialog,
          color: 'error',
          label: t('no_button'),
        },
        {
          id: 2,
          startIcon: <DoneIcon />,
          onClick: () => onArchiveTemplatesClickConfirm(activate),
          color: 'primary',
          label: t('yes_button'),
        },
      ],
    });
  };

  const onDeleteTemplatesClick = async () => {
    setDialogConfig({
      open: true,
      dialogTitle: t('delete_dialog'),
      dialogContent: t('delete_dialog_desc'),
      dialogActionsConfig: [
        {
          id: 1,
          startIcon: <HighlightOffIcon />,
          onClick: cancelCopyDialog,
          color: 'error',
          label: t('no_button'),
        },
        {
          id: 2,
          startIcon: <DoneIcon />,
          onClick: onDeleteTemplatesClickConfirm,
          color: 'primary',
          label: t('yes_button'),
        },
      ],
    });
  };

  useEffect(() => {
    setCheckboxSelection([]);
  }, [archiveFilter]);

  useEffect(() => {
    if (isCopyFinished) fetchTemplates();
  }, [isCopyFinished]);

  useEffect(() => {
    fetchTemplates();
  }, [paginationModel.page, paginationModel.pageSize, filterAndSortParams]);

  useEffect(() => {
    fetchTemplateDetails();
  }, [selectedTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TedTheme>
      <PreviewDialogTed
        fullScreen={false}
        templatePreview={templatePreview}
        onCancel={onPreviewCancel}
      />
      <Notification showStatuses={showStatuses} handleClose={handleClose} />
      <MyDialog
        open={dialogConfig?.open}
        dialogTitle={dialogConfig?.dialogTitle}
        dialogContent={dialogConfig?.dialogContent}
        dialogActionsConfig={dialogConfig?.dialogActionsConfig}
        maxWidth={dialogConfig?.maxWidth}
      />

      <UploadDialog
        importFile={importFile}
        isOpen={importDialogOpen}
        onCancel={cancelUploadDialog}
        onConfirm={importTemplates}
        onFileChange={handleFileUploadChange}
      />
      <Layout
        viewMode={viewMode}
        setViewMode={setViewMode}
        isRightDrawerOpen={isRightDrawerOpen}
        onRightDrawerClose={onRightDrawerClose}
        loading={isLoading}
        left={
          <Button
            icon='add'
            variant='outlined'
            onClick={() => onNewDocumentClick()}
            disabled={!isDetails(viewMode)}
            startIcon={<AddIcon />}
          >
            {t('new_template_button')}
          </Button>
        }
        middle={
          <TemplateList
            disabled={!isDetails(viewMode)}
            placeholder={t('template_empty_table_message')}
            data={templates ?? []}
            totalCount={templatesTotalCount ?? 0}
            loading={isLoading}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            selectedTemplate={selectedTemplate}
            handleRowClick={handleRowClick}
            goTo={goToTemplate}
            onRightDrawerOpen={onRightDrawerOpen}
            fetchEmailTemplatesPreview={fetchEmailTemplatesPreview}
            onSortChange={onSortChange}
            onFilterChange={onFilterChange}
            checkboxSelection={checkboxSelection}
            exportTemplates={exportTemplates}
            handleExportCheckboxChange={handleExportCheckboxChange}
            openUploadDialog={openUploadDialog}
            onDeleteTemplatesClick={onDeleteTemplatesClick}
            onArchive={onArchive}
            selectionArchiveValue={selectionArchiveValue}
          />
        }
        right={
          <TemplateDetails
            viewMode={viewMode}
            details={templateDetails}
            onCancelGoBack={() => setEditMode(VIEW_MODE_OPTIONS.DETAILS)}
            onSave={onEditTemplate}
            isLoading={isLoading}
            onEditClick={(mode) => setViewMode(mode)}
            onCopyClick={openCopyDialog}
            onRightDrawerClose={onRightDrawerClose}
          />
        }
      />
    </TedTheme>
  );
};

Templates.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({ id: PropTypes.string }),
  }).isRequired,
};

export default Templates;
